import React, { useState, useEffect } from 'react';
import { Modal, ModalDialog, ModalClose, DialogTitle, Button, Stack, Typography, Divider, Accordion, AccordionSummary, AccordionDetails, Box } from '@mui/joy';
import Parameter from './Parameter';
import PDGSearchbar from './PDGSearchbar';
import { getDefaultValuesForSchema, validateParam } from '../Internal/schemaDefaults';
// Object cache is now handled globally via Redux

export default function LineshapeParameterModal(props) {
    const { open, onClose, onSave, lineshapeSchema, initialValues } = props;
    
    const [parameterValues, setParameterValues] = useState({
        fixed: {},
        optimization: {}
    });

    // Initialize parameter values from defaults or initial values
    useEffect(() => {
        if (lineshapeSchema) {
            const newValues = {
                fixed: {},
                optimization: {}
            };

            // Initialize fixed parameters
            newValues.fixed = getDefaultValuesForSchema(
                lineshapeSchema.fixed_parameters,
                initialValues?.fixed_parameters
            );

            // Initialize optimization parameters
            newValues.optimization = getDefaultValuesForSchema(
                lineshapeSchema.optimization_parameters,
                initialValues?.optimization_parameters
            );

            setParameterValues(newValues);
        }
    }, [lineshapeSchema, initialValues]);

    const handleParameterChange = (category, key, value) => {
        setParameterValues(prev => ({
            ...prev,
            [category]: {
                ...prev[category],
                [key]: value
            }
        }));
    };

    const handlePDGParticleSelect = (particleData) => {
        // Special handling for RelativisticBreitWigner
        if (lineshapeSchema.lineshape_type === 'RelativisticBreitWigner') {
            setParameterValues(prev => {
                const newValues = { ...prev };
                
                // Ensure optimization object exists
                if (!newValues.optimization) {
                    newValues.optimization = {};
                }
                
                // Update pole_mass if available
                if (particleData.mass !== undefined && particleData.mass !== null) {
                    newValues.optimization = {
                        ...newValues.optimization,
                        pole_mass: particleData.mass
                    };
                }
                
                // Update width if available
                if (particleData.width !== undefined && particleData.width !== null) {
                    newValues.optimization = {
                        ...newValues.optimization,
                        width: particleData.width
                    };
                }
                
                return newValues;
            });
        }
    };

    // Validation function to check if all required parameters are set
    const validateParameters = () => {
        const errors = [];

        // Check fixed parameters
        if (lineshapeSchema.fixed_parameters) {
            Object.entries(lineshapeSchema.fixed_parameters).forEach(([key, param]) => {
                errors.push(...validateParam(key, param, parameterValues.fixed[key]));
            });
        }

        // Check optimization parameters
        if (lineshapeSchema.optimization_parameters) {
            Object.entries(lineshapeSchema.optimization_parameters).forEach(([key, param]) => {
                errors.push(...validateParam(key, param, parameterValues.optimization[key]));
            });
        }

        return errors;
    };

    const handleSave = () => {
        const validationErrors = validateParameters();
        
        if (validationErrors.length > 0) {
            // Show validation errors (you could display these in a toast or alert)
            console.error('Validation errors:', validationErrors);
            alert('Please fill in all required parameters:\n' + validationErrors.join('\n'));
            return;
        }
        
        const result = {
            lineshape_type: lineshapeSchema.lineshape_type,
            fixed_parameters: parameterValues.fixed,
            optimization_parameters: parameterValues.optimization,
            description: lineshapeSchema.description
        };
        onSave(result);
    };

    if (!lineshapeSchema) return null;

    const hasFixedParams = lineshapeSchema.fixed_parameters && Object.keys(lineshapeSchema.fixed_parameters).length > 0;
    const hasOptParams = lineshapeSchema.optimization_parameters && Object.keys(lineshapeSchema.optimization_parameters).length > 0;
    
    // Check validation status
    const validationErrors = validateParameters();
    const isValid = validationErrors.length === 0;

    return (
        <Modal 
            open={open} 
            onClose={onClose}
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
                <ModalDialog 
                    sx={{ 
                        maxWidth: 700, 
                        maxHeight: '85vh', 
                        overflow: 'auto',
                        borderRadius: 'lg',
                        boxShadow: 'lg'
                    }}
                >
                <ModalClose onClick={onClose} />
                <DialogTitle sx={{ 
                    fontSize: '1.5rem', 
                    fontWeight: 'lg',
                    color: 'primary.700',
                    borderBottom: '1px solid',
                    borderColor: 'neutral.200',
                    pb: 2,
                    mb: 3
                }}>
                    {lineshapeSchema.lineshape_type} Configuration
                </DialogTitle>
                
                <Typography 
                    level="body-sm" 
                    color="neutral" 
                    sx={{ 
                        marginBottom: 3,
                        p: 2,
                        backgroundColor: 'neutral.50',
                        borderRadius: 'md',
                        border: '1px solid',
                        borderColor: 'neutral.200',
                        fontStyle: 'italic'
                    }}
                >
                    {lineshapeSchema.description}
                </Typography>

                {/* PDG Searchbar for RelativisticBreitWigner */}
                {lineshapeSchema.lineshape_type === 'RelativisticBreitWigner' && (
                    <Box 
                        sx={{ 
                            p: 3, 
                            backgroundColor: 'primary.50',
                            borderRadius: 'md',
                            border: '1px solid',
                            borderColor: 'primary.200',
                            mb: 3
                        }}
                    >
                        <Stack spacing={2}>
                            <Typography level="title-sm" sx={{ color: 'primary.700', fontWeight: 'lg' }}>
                                Quick Fill from PDG Database
                            </Typography>
                            <Typography level="body-xs" color="neutral">
                                Select a particle from the PDG database to automatically fill the pole mass and width parameters.
                            </Typography>
                            <PDGSearchbar 
                                onParticleSelect={handlePDGParticleSelect}
                                includeMass={true}
                                includeWidth={true}
                            />
                        </Stack>
                    </Box>
                )}

                <Stack spacing={4}>
                    {/* Fixed Parameters Section */}
                    {hasFixedParams && (
                        <Accordion 
                            defaultExpanded
                            sx={{
                                '& .MuiAccordionSummary-root': {
                                    backgroundColor: 'primary.50',
                                    borderRadius: 'md',
                                    border: '1px solid',
                                    borderColor: 'primary.200'
                                }
                            }}
                        >
                            <AccordionSummary>
                                <Typography level="title-md" sx={{ color: 'primary.700', fontWeight: 'lg' }}>
                                    Fixed Parameters
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 3 }}>
                                <Stack spacing={3}>
                                    {Object.entries(lineshapeSchema.fixed_parameters).map(([key, param]) => (
                                        <Parameter
                                            key={key}
                                            paramKey={key}
                                            paramSchema={param}
                                            value={parameterValues.fixed[key]}
                                            onChange={(newValue) => handleParameterChange('fixed', key, newValue)}
                                            level={0}
                                        />
                                    ))}
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {/* Optimization Parameters Section */}
                    {hasOptParams && (
                        <Accordion 
                            defaultExpanded
                            sx={{
                                '& .MuiAccordionSummary-root': {
                                    backgroundColor: 'success.50',
                                    borderRadius: 'md',
                                    border: '1px solid',
                                    borderColor: 'success.200'
                                }
                            }}
                        >
                            <AccordionSummary>
                                <Typography level="title-md" sx={{ color: 'success.700', fontWeight: 'lg' }}>
                                    Optimization Parameters
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 3 }}>
                                <Stack spacing={3}>
                                    {Object.entries(lineshapeSchema.optimization_parameters).map(([key, param]) => (
                                        <Parameter
                                            key={key}
                                            paramKey={key}
                                            paramSchema={param}
                                            value={parameterValues.optimization[key]}
                                            onChange={(newValue) => handleParameterChange('optimization', key, newValue)}
                                            level={0}
                                        />
                                    ))}
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </Stack>

                <Divider sx={{ marginTop: 3, marginBottom: 3 }} />

                <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 2 }}>
                    <Button 
                        variant="outlined" 
                        onClick={onClose}
                        sx={{ minWidth: 100 }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSave}
                        disabled={!isValid}
                        sx={{ minWidth: 140 }}
                        color={isValid ? "primary" : "neutral"}
                    >
                        Save Configuration
                    </Button>
                </Stack>
                
                {/* Validation feedback */}
                {!isValid && (
                    <Stack spacing={1} sx={{ mt: 2, p: 2, backgroundColor: 'danger.50', borderRadius: 'md', border: '1px solid', borderColor: 'danger.200' }}>
                        <Typography level="body-sm" color="danger" sx={{ fontWeight: 'md' }}>
                            Please complete all required parameters:
                        </Typography>
                        {validationErrors.map((error, index) => (
                            <Typography key={index} level="body-xs" color="danger" sx={{ ml: 1 }}>
                                • {error}
                            </Typography>
                        ))}
                    </Stack>
                )}
            </ModalDialog>
        </Modal>
    );
}

